import { useState, useMemo, useCallback, useEffect } from 'react'
import { Table, Input, Select } from 'antd'
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import ChannelInput from '../../../Components/Omnichannel/ChannelInput'
import ChannelCommissionsRangeRow from '../../../Components/Omnichannel/ChannelCommissionsRangeRow'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  filterGeneralDistinct,
  getHtmlParentElement,
  isProdSite,
  renderTranslation,
  stopPropagation,
} from '../../../Helpers/GenericHelpers'
import { DateFormat } from '../../../Constants/Configuration'
import {
  ChannelTableCols,
  channelTableCommission,
  channelTableCommissionsRange,
  channelTableFixedImport,
  channelTableId,
  channelTablePriceList,
  channelTableModifiedDate,
  channelTableName,
  channelTableTransport,
  channelTableMultiShipping,
} from '../../../Constants/TableConfiguration'
import { setCapitalLetter } from '../../../Helpers/StringHelpers'
import { setChannelPropertyToSave } from '../../../Slices/Settings/ChannelSlice'
import { getChannelFilters, getChannels, getCountries } from '../../../Services/Omnichannel/ChannelService'
import { retrieveCommission, retrieveRangeThresholds } from '../../../Helpers/ChannelSettingsHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import ShippingModal from './ShippingModal'

const { Option } = Select

function Channels(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const channelList = useAppSelector(state => state.present.channel.channelList)
  const listings = useAppSelector(state => state.present.configuration.allPricelists)
  const [channelSearch, setChannelSearch] = useState('')
  const [expandedRows, setExpandedRows] = useState([])
  const [shippingModal, setShippingModal] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState(null)
  const enableMoreColumns: Boolean = true //per disabilitare momentaneamente le colonne

  useEffect(() => {
    dispatch(getChannels())
    dispatch(getChannelFilters())
    dispatch(getCountries())
  }, [dispatch])

  const filterBySearchValue = useCallback(
    channelData => {
      if (!channelSearch || channelSearch.length < 2) return channelData

      const lowerSearchValue = channelSearch.toLowerCase()
      return channelData.filter(x =>
        x[channelTableName] ? x[channelTableName].toLowerCase().includes(lowerSearchValue) : false,
      )
    },
    [channelSearch],
  )

  const setChannelChanges = useCallback(
    (name, field, value) => {
      dispatch(
        setChannelPropertyToSave({
          name: name,
          field: field,
          value: value,
          date: new Date().toISOString(),
        }),
      )
    },
    [dispatch],
  )

  const channelTableColumns = useMemo(() => {
    const channelColumns = []
    ChannelTableCols.forEach(x => {
      if (x === channelTableName) {
        channelColumns.push({
          title: (
            <div className='channel-table-search'>
              <Input
                variant={'borderless'}
                prefix={<SearchOutlined className='general-icon input-search-icon' />}
                placeholder={renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])}
                className='input-search channel-search'
                value={channelSearch}
                onChange={e => {
                  setChannelSearch(e.target.value)
                }}
                onClick={stopPropagation}
                allowClear={{
                  clearIcon: <CloseOutlined className='general-icon input-clear-icon' />,
                }}
              />
            </div>
          ),
          dataIndex: x,
          key: x,
          width: 240, //same width in .channel-table-search OmnichannelSeasonRules
          render: text => {
            return (
              <div>
                <div className='channel-text'>{text}</div>
              </div>
            )
          },
        })
      } else if (x === channelTablePriceList) {
        channelColumns.push({
          title: <div>{renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])}</div>,
          dataIndex: x,
          key: x,
          render: (text, record) => {
            return (
              <Select
                value={text}
                className={enableMoreColumns ? 'w-120-p' : 'w-100'}
                variant={'borderless'}
                onChange={value => {
                  setChannelChanges(record[channelTableName], x, value)
                }}
                getPopupContainer={getHtmlParentElement}
              >
                {listings &&
                  listings.map(x => {
                    return (
                      <Option key={record.name + '-' + x.name} value={x.name}>
                        {x.name}
                      </Option>
                    )
                  })}
              </Select>
            )
          },
        })
      } else if (!isProdSite()) {
        if (x === channelTableFixedImport && enableMoreColumns) {
          channelColumns.push({
            title: <div>{renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])} € </div>,
            dataIndex: x,
            key: x,
            render: (text, record) => {
              return <ChannelInput channelName={record.name} field={x} max={Number.MAX_SAFE_INTEGER} />
            },
          })
        } else if (x === channelTableTransport && enableMoreColumns) {
          channelColumns.push({
            title: <div>{renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])}</div>,
            dataIndex: x,
            key: x,
            width: 290,
            render: (text, record) => {
              const fixedShipping = record.fixedShipping
              const exceptionsNumber = record.multiShipping.length
              const addMultishippingText = renderTranslation(
                language,
                TranslationKeys.multishippingExceptionsAddException,
              )
              const shippingText =
                exceptionsNumber > 0
                  ? ` / ${exceptionsNumber} ${renderTranslation(language, TranslationKeys.multishippingExceptionsSet)}`
                  : ''
              return (
                <div className='d-flex align-center ma-0 pa-0'>
                  <ChannelInput
                    channelName={record.name}
                    field={x}
                    value={fixedShipping}
                    max={Number.MAX_SAFE_INTEGER}
                  />
                  <div
                    className='ma-0 pa-0 pl-2 cursor-pointer'
                    onClick={() => {
                      setShippingModal(true)
                      setSelectedChannel(record.name)
                    }}
                  >
                    <span>{addMultishippingText + shippingText}</span>
                  </div>
                </div>
              )
            },
          })
        } else if (x === channelTableCommission && enableMoreColumns) {
          channelColumns.push({
            title: <div>{renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])} %</div>,
            dataIndex: x,
            key: x,
            render: (text, record) => {
              const commissions = record.commissions ? record.commissions : []
              const commission = retrieveCommission(commissions)
              const currentRangesThreshold = retrieveRangeThresholds(commissions)
              return currentRangesThreshold.length > 0 ? null : (
                <ChannelInput
                  className={'commissions-input'}
                  channelName={record.name}
                  value={commission?.value}
                  max={100}
                />
              )
            },
          })
        } else if (x === channelTableCommissionsRange && enableMoreColumns) {
          channelColumns.push({
            title: <div>{renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])}</div>,
            dataIndex: x,
            key: x,
            render: (text, record) => {
              const currentComissions = record.commissions ? record.commissions : []
              const currentRangesThreshold = retrieveRangeThresholds(currentComissions)
              const rangeThresholdToDisplay = currentRangesThreshold.map(x => x.threshold).filter(filterGeneralDistinct)
              const rangeLength =
                rangeThresholdToDisplay.length > 0 ? rangeThresholdToDisplay.length + 1 : rangeThresholdToDisplay.length
              //+ 1 when there is ranges stand for other column

              return (
                <div
                  onClick={() => {
                    if (expandedRows.indexOf('key-' + record.name) === -1) {
                      setExpandedRows([...expandedRows, 'key-' + record.name])
                    } else {
                      setExpandedRows(oldArray => {
                        const filteredResults = oldArray.filter(x => x !== 'key-' + record.name)
                        return [...filteredResults]
                      })
                    }
                  }}
                >
                  {renderTranslation(language, TranslationKeys.channelTableSetRange)}
                  {rangeLength > 0
                    ? ' / ' + rangeLength + ' ' + renderTranslation(language, TranslationKeys.channelTableRangeSet)
                    : ''}
                </div>
              )
            },
          })
        } else if (x === channelTableModifiedDate) {
          channelColumns.push({
            title: <div>{renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])}</div>,
            dataIndex: x,
            key: x,
            render: (text, record) => {
              return <div className='channel-modified-date'>{dayjs(text).format(DateFormat)}</div>
            },
          })
        } else if (enableMoreColumns) {
          channelColumns.push({
            title: <div>{renderTranslation(language, TranslationKeys['channelTable' + setCapitalLetter(x)])}</div>,
            dataIndex: x,
            key: x,
            render: (text, record) => {
              return text
            },
          })
        }
      }
    })

    return channelColumns
  }, [language, channelSearch, listings, setChannelChanges, expandedRows])

  const channelTableData = useMemo(() => {
    const channels = [...channelList]
    return channels && channels.length > 0
      ? channels
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(channel => {
            return {
              [channelTableId]: channel[channelTableId],
              [channelTableName]: channel[channelTableName],
              [channelTablePriceList]: channel[channelTablePriceList],
              [channelTableFixedImport]: enableMoreColumns ? channel[channelTableFixedImport] : [],
              [channelTableTransport]: enableMoreColumns ? channel[channelTableTransport] : [],
              [channelTableMultiShipping]: enableMoreColumns ? channel[channelTableMultiShipping] : [],
              [channelTableCommission]: enableMoreColumns ? channel[channelTableCommission] : [],
              [channelTableCommissionsRange]: enableMoreColumns
                ? channel[channelTableCommissionsRange]
                  ? channel[channelTableCommissionsRange]
                  : []
                : [],
              [channelTableModifiedDate]: channel[channelTableModifiedDate],
            }
          })
      : []
  }, [channelList])

  return (
    <>
      <Table
        id='channel-table'
        columns={channelTableColumns}
        rowKey={record => {
          return 'key-' + record.name
        }}
        expandable={{
          expandedRowRender: record => {
            return <ChannelCommissionsRangeRow id={record.name} expandedRows={expandedRows} />
          },
          rowExpandable: record => true,
          expandedRowKeys: expandedRows,
          showExpandColumn: false,
          columnWidth: 100,
        }}
        dataSource={filterBySearchValue(channelTableData)}
        pagination={false}
        tableLayout='fixed'
      />
      <ShippingModal
        bodyref={props.bodyref}
        selectedChannel={selectedChannel}
        setShippingModal={setShippingModal}
        shippingModal={shippingModal}
      />
    </>
  )
}

export default Channels
